import moment from "moment";
import Logger from "neon-frontend-utils/src/logger";
import Components from "../../../constants/Components";
import OrganisationHierarchyTypes from "../../../constants/OrganisationHierarchyTypes";
import * as actionType from "./../constants/ActionTypes";

const initialState = {
    addOnPrices: {},
    invoices: [],
    invoicesLoaded: false,
    organisationProjects: {},
    organisations: [],
    prices: {},
};

/**
 * Filters out invalid date ranges from the prices while keeping non-date range prices
 *
 * @param {Object[]} prices - prices
 *
 * @returns {Object[]} filtered prices
 */
function filterInvalidDateRange(prices) {
    return prices?.filter((price) => {
        if (!price.start || !price.end) {
            return true;
        }

        const startDate = moment(price.start);
        const endDate = moment(price.end);

        return startDate.isBefore(endDate);
    });
}

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {{}} new state
 */
export default function financeReducer(state = initialState, action) {
    const newState = Object.assign({}, state);

    switch (action.type) {
        case actionType.GET_ORGANISATIONS_AND_PROJECTS: {
            // clone state
            newState.organisations = [];
            newState.organisationProjects = {};

            // add received tree nodes to newState (if applicable, add received nodes as children of the parent on which the new node was added)
            Object.keys(action.response).forEach((nodeKey) => {
                const node = action.response[nodeKey];

                if (node.type === OrganisationHierarchyTypes.ORGANISATION) {
                    newState.organisations.push({
                        depth: node.depth,
                        id: node.id,
                        key: node.slug,
                        label: `${node.name}`,
                    });
                } else if (
                    node.type === OrganisationHierarchyTypes.JOB_FUNCTION ||
                    node.type === OrganisationHierarchyTypes.PROJECT
                ) {
                    const pathParts = nodeKey.split("/");
                    const parent = pathParts[pathParts.length - 2];

                    if (!newState.organisationProjects[parent]) {
                        newState.organisationProjects[parent] = [];
                    }

                    newState.organisationProjects[parent].push({
                        key: node.slug,
                        label: `${node.name}`,
                        type: node.type,
                    });
                }
            });

            break;
        }

        case actionType.GET_INVOICES: {
            try {
                // clear current items from newState
                newState.invoices = [];

                // loop through newly retrieved items from the action and add to the newState
                action.response.forEach((invoice) => {
                    const {
                        addedLog,
                        organisationName,
                        projectName,
                        email,
                        date,
                        billedDate,
                        afasId,
                        details,
                        invoiceId,
                        participantName,
                        overallInvoiceName,
                        owner,
                        ownerId,
                        costHeading,
                    } = invoice;

                    // build list view
                    newState.invoices.push({
                        addedLog,
                        afasProjectId: afasId || null,
                        // dates need to be formatted to YYYY-MM-DD so that the sort function works correctly
                        billedDate: billedDate?.date ? moment(billedDate.date).format("YYYY-MM-DD") : null,
                        completionDate: date.date ? moment(date.date).format("YYYY-MM-DD") : null,
                        costHeading,
                        details,
                        email,
                        invoiceId,
                        organisationName,
                        overallInvoiceName,
                        owner,
                        ownerId: ownerId || null,
                        participantName,
                        projectName,
                        totalPrice: details.reduce((sum, item) => sum + item.price, 0),
                    });
                });

                newState.invoicesLoaded = true;
            } catch (error) {
                Logger.instance.error({
                    component: Components.FINANCE,
                    error,
                    message: "Exception in finance reducer GET_INVOICES",
                });
            }

            break;
        }

        case actionType.GET_PRICES: {
            try {
                // clear current items from newState
                newState.prices = initialState.prices;

                const prices = {};

                const filteredPrices = filterInvalidDateRange(action.response);

                filteredPrices.forEach((price) => {
                    const year = moment(price.start, "YYYY-MM-DD").format("YYYY");

                    // each year is an object
                    if (!prices[year]) {
                        prices[year] = {};
                    }

                    const productIdWithPhase = price.phase ? `${price.product.id}_${price.phase}` : price.product.id;

                    // for each product a new object is created containing the prices
                    if (!prices[year][productIdWithPhase]) {
                        prices[year][productIdWithPhase] = {
                            name: price.product.productHandle,
                            phase: price.phase,
                            prices: [],
                        };
                    }

                    // each price is a separate object in the prices array, containing min/max and a value
                    prices[year][productIdWithPhase].prices.push({
                        max: price.max,
                        min: price.min,
                        price: price.price,
                    });
                });

                newState.prices = prices;
            } catch (error) {
                Logger.instance.error({
                    component: Components.FINANCE,
                    error,
                    message: "Exception in Participants reducer GET_PRICES",
                });
            }

            break;
        }

        case actionType.GET_ADD_ON_PRICES: {
            try {
                // clear current items from newState
                newState.addOnPrices = initialState.addOnPrices;

                const addOnPrices = {};

                const filteredPrices = filterInvalidDateRange(action.response);

                filteredPrices.forEach((price) => {
                    const year = moment(price.start, "YYYY-MM-DD").format("YYYY");

                    // each year is an object
                    if (!addOnPrices[year]) {
                        addOnPrices[year] = {};
                    }

                    // for each product a new object is created containing the prices
                    if (!addOnPrices[year][price.productAddOn.id]) {
                        addOnPrices[year][price.productAddOn.id] = {
                            name: price.productAddOn.productAddOnHandle,
                            prices: [],
                        };
                    }

                    // each price is a separate object in the prices array, containing min/max and a value
                    addOnPrices[year][price.productAddOn.id].prices.push({
                        max: price.max,
                        min: price.min,
                        price: price.price,
                    });
                });

                newState.addOnPrices = addOnPrices;
            } catch (error) {
                Logger.instance.error({
                    component: Components.FINANCE,
                    error,
                    message: "Exception in Participants reducer GET_ADD_ON_PRICES",
                });
            }

            break;
        }

        case actionType.CLEAR_INVOICES: {
            newState.invoices = initialState.invoices;
            newState.invoicesLoaded = initialState.invoicesLoaded;

            break;
        }

        case actionType.CLEAR_PRICES: {
            newState.prices = initialState.prices;
            newState.addOnPrices = initialState.addOnPrices;

            break;
        }

        case actionType.SET_BILLED_DATE: {
            action.selectedInvoices.forEach((id) => {
                const invoiceToUpdate = newState.invoices.find((invoice) => invoice.invoiceId === id);

                if (invoiceToUpdate) {
                    invoiceToUpdate.billedDate = action.billedDate;
                }
            });
            break;
        }

        default:
            break;
    }

    return newState;
}
